import useSpaceStore from '../../stores/spaceStore';
import { SpaceSettings, UISpace } from '../../types/spaces';
import { AnyWindowEntity, WindowEntity } from '../../types/windows';
import { CRDTServiceWS, getCRDTServiceInstance } from './crdtServiceWS';
import { debounce } from 'lodash';
import { spaceService } from '../spaceService';
import { createFileLogger } from '../../utils/logger';
import * as Y from '../../shared/yjs';
import { usePresenceStore } from '../../stores/simplePresenceStore';
import { getCurrentUserId } from '../../utils/userIdUtils';

const log = createFileLogger('SPACE_CRDT_SERVICE', { enabled: true });

/**
 * Sort a raw tab array by an explicit tabOrder array.
 * Tabs missing from tabOrder (written by older clients) are appended at the end
 * so existing users see a graceful migration — their tabs stay visible even if
 * the order wasn't persisted yet.
 */
function sortTabsByOrder(rawTabs: any[], tabOrder: string[] | undefined): any[] {
  if (!tabOrder || tabOrder.length === 0) return rawTabs;
  const byId = new Map(rawTabs.map(t => [t.id, t]));
  const ordered = tabOrder.map(id => byId.get(id)).filter((t): t is any => !!t);
  const orderedIds = new Set(tabOrder);
  const remaining = rawTabs.filter(t => !orderedIds.has(t.id));
  return [...ordered, ...remaining];
}

// Create a single CRDT service instance
const crdtServiceWS = getCRDTServiceInstance();

// Add a high-performance local event bus for window position updates
// This avoids the CRDT overhead during dragging while maintaining local responsiveness
const windowPositionEventBus = {
  // Track last updates to prevent duplicates within short time spans
  lastUpdates: new Map<string, {position: {x: number, y: number}, timestamp: number}>(),
  
  // Use a debounce time in ms to prevent too frequent updates
  updateDebounceTime: 16,
  
  publish: (spaceId: string, windowId: string, position: { x: number; y: number }) => {
    const now = performance.now();
    const updateKey = `${windowId}`;
    const lastUpdate = windowPositionEventBus.lastUpdates.get(updateKey);
    
    // Skip if too similar to last update within debounce time
    if (lastUpdate && 
        now - lastUpdate.timestamp < windowPositionEventBus.updateDebounceTime &&
        Math.abs(lastUpdate.position.x - position.x) < 1 &&
        Math.abs(lastUpdate.position.y - position.y) < 1) {
      return;
    }
    
    // Update the last position
    windowPositionEventBus.lastUpdates.set(updateKey, {
      position,
      timestamp: now
    });
    
    // Dispatch a custom event for immediate local UI updates
    window.dispatchEvent(new CustomEvent('windowPositionUpdate', {
      detail: { spaceId, windowId, position }
    }));
  },
  
  subscribe: (callback: (detail: { spaceId: string, windowId: string, position: { x: number, y: number } }) => void) => {
    const handler = (e: any) => callback(e.detail);
    window.addEventListener('windowPositionUpdate', handler);
    return () => window.removeEventListener('windowPositionUpdate', handler);
  }
};

// PERFORMANCE FIX: Add throttling for CRDT position updates during drag
const throttledCRDTUpdates = new Map<string, {
  lastUpdate: number;
  pendingUpdate: boolean;
  throttleMs: number;
}>();

// Helper function to check if we should throttle CRDT updates
const shouldThrottleCRDTUpdate = (windowId: string, isDragOperation: boolean): boolean => {
  if (!isDragOperation) return false; // Don't throttle non-drag updates
  
  const now = performance.now();
  const throttleState = throttledCRDTUpdates.get(windowId);
  const throttleMs = 500; // Throttle CRDT updates during drag
  
  if (!throttleState) {
    throttledCRDTUpdates.set(windowId, {
      lastUpdate: now,
      pendingUpdate: false,
      throttleMs
    });
    return false; // Allow first update
  }
  
  const timeSinceLastUpdate = now - throttleState.lastUpdate;
  if (timeSinceLastUpdate < throttleMs) {
    // Schedule a final update if we don't have one pending
    if (!throttleState.pendingUpdate) {
      throttleState.pendingUpdate = true;
      setTimeout(() => {
        throttleState.lastUpdate = performance.now();
        throttleState.pendingUpdate = false;
        // Allow the final update to go through
      }, throttleMs - timeSinceLastUpdate);
    }
    return true; // Throttle this update
  }
  
  // Update timestamp and allow update
  throttleState.lastUpdate = now;
  return false;
};

// Create debounced save function for persisting space data to backend
const debouncedSaveSpaceToBackend = debounce(async (spaceId: string) => {
  try {
    const store = useSpaceStore.getState();
    const windows = store.windows;
    const settings = store.activeSpace?.settings;
    const activeWindowId = store.activeWindowId;
    const contextEntities = store.contextEntities; // ✅ NEW: Include contextEntities
    
    log.info(`[SpaceCRDT] Persisting space ${spaceId} to backend with ${Object.keys(windows).length} windows and ${Object.keys(contextEntities || {}).length} context entities`);
    await spaceService.updateSpace(spaceId, windows, settings || {}, activeWindowId, contextEntities);
  } catch (error) {
    log.error('[SpaceCRDT] Error persisting space to backend:', error);
  }
}, 1000); // Debounce to reduce database load

// Add debounced save for changes to main space data, used for all window updates
const debouncedSaveWindowToSpace = debounce((spaceId: string, windowId: string, updates: Partial<AnyWindowEntity>) => {
  try {
    log.info('[SpaceCRDT] Triggering backend save for window update');
    // DON'T call updateWindow again - that creates a loop and can corrupt state!
    // The window has already been updated locally by the calling code.
    // Just trigger the database save.
    debouncedSaveSpaceToBackend(spaceId);
  } catch (error) {
    log.error('[SpaceCRDT] Error triggering backend save:', error);
  }
}, 300); // Shorter debounce for window updates

export interface SpaceUpdate {
  windows?: AnyWindowEntity[];
  activeWindowId?: string;
  layout?: any;
  settings?: {
    isContentsVisible?: boolean;
    isChatVisible?: boolean;
    bgColor?: string;
    viewMode?: 'spatial' | 'focused';
    sidebar?: {
      isVisible?: boolean;
      width?: number;
      mode?: 'normal' | 'overlay';
    };
  };
}

interface SpaceCRDTData {
  windows: AnyWindowEntity[];
  timestamp: number;
}

export interface SpaceConnectionInfo {
  spaceId: string;
  connectionCount: number;
  isMultiTab: boolean;
  providerStatus?: string;
  lastUpdate: number;
}

export class SpaceCRDTService {
  private settingsListeners: Map<string, ((settings: Partial<SpaceSettings>) => void)[]> = new Map();
  private spaceUpdateListeners: Map<string, ((update: SpaceUpdate) => void)[]> = new Map();
  initializedSpaces = new Set<string>();
  // Full CRDT keys captured at initialization time so disconnectAll can find
  // the right providers even if getCurrentUserId() returns null at logout.
  private initializedSpaceKeys = new Map<string, { spaceKey: string; positionKey: string }>();
  private removingWindows = new Set<string>(); // Track windows being removed
  private connectionListeners: Map<string, ((connectionInfo: SpaceConnectionInfo) => void)[]> = new Map(); // Track connection listeners
  private connectionPollIntervals: Map<string, ReturnType<typeof setInterval>> = new Map(); // One poller per spaceId
  private spaceObservers = new Map<string, () => void>(); // Track CRDT observers for cleanup
  private positionReceivers = new Map<string, () => void>(); // Track position receiver cleanups
  private localOriginId = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`; // Unique ID for this tab

  constructor(private crdt: CRDTServiceWS) {
    // Set up window position update listener for immediate local UI updates
    this.setupWindowPositionListener();
    
    // Add a global test function for debugging
    (window as any).crdt = {
      testWindowPosition: (windowId: string, x: number, y: number) => {
        const spaceId = useSpaceStore.getState().activeSpace?.id;
        if (!spaceId) {
          log.error("[Test] No active space");
          return;
        }
        
        log.info(`[Test] Updating window ${windowId} position to (${x}, ${y})`);
        
        // Update the window position
        this.updateWindow(spaceId, windowId, { position: { x, y } });
      },
      
      dumpWindowPositions: () => {
        const windows = useSpaceStore.getState().windows;
        const positions: Record<string, {x: number, y: number}> = {};
        
        Object.entries(windows).forEach(([id, win]: [string, any]) => {
          if (win.position) {
            positions[id] = win.position;
          }
        });
        
        log.info("[CRDT Debug] Current window positions:", positions);
        return positions;
      },
      
      verifyPersistence: async () => {
        const spaceId = useSpaceStore.getState().activeSpace?.id;
        if (!spaceId) {
          log.error("[Test] No active space");
          return;
        }

        try {
          // Force an immediate save to backend
          await debouncedSaveSpaceToBackend.flush();
          log.info("[Test] Forced immediate save to backend");
          
          // Fetch the space directly from backend
          const backendSpace = await spaceService.getSpace(spaceId);
          
          // Check if we got a valid response
          if (!backendSpace) {
            log.error("[Test] Failed to fetch space from backend");
            return { error: "Failed to fetch space from backend" };
          }
          
          // Get current window positions from store
          const storeWindows = useSpaceStore.getState().windows;
          
          // Compare
          log.info("[Test] Backend windows:", backendSpace.windows);
          log.info("[Test] Store windows:", storeWindows);
          
          // Check for any windows in store but not in backend
          const missingInBackend = Object.keys(storeWindows).filter(id => 
            !backendSpace.windows[id]);
            
          if (missingInBackend.length > 0) {
            log.error("[Test] Windows in store but missing in backend:", missingInBackend);
          } else {
            log.info("[Test] All windows in store are persisted in backend");
          }
          
          // Check window positions
          let positionMismatch = false;
          Object.entries(storeWindows).forEach(([id, window]: [string, any]) => {
            const backendWindow = backendSpace.windows[id];
            if (backendWindow) {
              if (window.position && backendWindow.position &&
                  (Math.abs(window.position.x - backendWindow.position.x) > 1 ||
                   Math.abs(window.position.y - backendWindow.position.y) > 1)) {
                log.error(`[Test] Position mismatch for window ${id}:`, 
                  {store: window.position, backend: backendWindow.position});
                positionMismatch = true;
              }
            }
          });
          
          if (!positionMismatch) {
            log.info("[Test] All window positions match between store and backend");
          }
          
          return {
            backendSpace,
            storeWindows
          };
        } catch (error) {
          log.error("[Test] Error verifying persistence:", error);
          throw error;
        }
      }
    };
  }

  private setupWindowPositionListener() {
    windowPositionEventBus.subscribe(({spaceId, windowId, position}) => {
      // IMPORTANT: We need to detect update loops here too
      if ((window as any).__isUpdatingStore) {
        return;
      }

      // Set flag to prevent circular updates
      (window as any).__isUpdatingStore = true;
      
      // Directly update the store without going through CRDT
      try {
        const store = useSpaceStore.getState();
        
        // Check if window exists before updating
        if (!store.windows[windowId]) {
          return;
        }
        
        // Use the internal helper with source: 'local' but syncCRDT: false
        // This prevents triggering another CRDT sync (which would cause loops)
        store._applyLocalWindowUpdate(windowId, { position }, {
          source: 'local',
          syncCRDT: false,
          log: false
        });
      } finally {
        // Always reset the flag
        (window as any).__isUpdatingStore = false;
      }
    });
  }

  // Connect to the window position updates CRDT key to receive live updates from other clients
  setupWindowPositionReceiver(spaceId: string) {
    const positionKey = this.getPositionKey(spaceId);
    
    // Use the connect method with no debounce to get instant position updates
    return this.crdt.connect(positionKey, (update) => {
      if (!update.data) return;
      
      // Prevent circular updates
      if ((window as any).__isUpdatingStore) return;
      
      // New format directly maps windowIds to position objects
      Object.entries(update.data).forEach(([windowId, position]) => {
        // Skip processing if this is the position we just sent
        if ((window as any).__lastDragWindowId === windowId && 
            (window as any).__lastDragPosition &&
            (window as any).__lastDragPosition.x === (position as any).x &&
            (window as any).__lastDragPosition.y === (position as any).y) {
          return;
        }
        
        // Set the flag to prevent circular updates
        (window as any).__isUpdatingStore = true;
        
        try {
          // Publish to the event bus first for immediate UI updates
          windowPositionEventBus.publish(spaceId, windowId, position as any);
          
          // Then update the store for persistence
          const store = useSpaceStore.getState();
          
          // Check if window exists before updating
          if (store.windows[windowId]) {
            // Use the internal helper with source: 'crdt' and syncCRDT: false
            // This is a CRDT update from another client, so don't sync back
            store._applyLocalWindowUpdate(windowId, { position: position as any }, {
              source: 'crdt',
              syncCRDT: false,
              log: false
            });
          }
          
          // Ensure the update also gets persisted to the database
          debouncedSaveWindowToSpace(spaceId, windowId, { position: position as any });
        } finally {
          // Always reset the flag
          (window as any).__isUpdatingStore = false;
        }
      });
    });
  }

  hasListener(spaceId: string): boolean {
    const listeners = this.spaceUpdateListeners.get(spaceId);
    return !!listeners && listeners.length > 0;
  }

  getRemovingWindows(): Set<string> {
    return this.removingWindows;
  }

  onSpaceUpdate(spaceId: string, callback: (update: SpaceUpdate) => void) {
    return this.crdt.connect(this.getKey(spaceId), (update: any) => {
      // Make sure we're getting and sending the full windows array
      if (update.data?.windows) {
        callback({
          ...update.data,
          windows: update.data.windows
        });
      }
    });
  }

  observeSpace(spaceId: string, onUpdate: (update: UISpace) => void) {
    const key = this.getKey(spaceId);
    
    return this.crdt.connect<UISpace>(key, (update) => {
      if (update.data) {
        onUpdate(update.data);
      }
    });
  }

  removeWindow(spaceId: string, windowId: string) {
    log.info('[SpaceCRDT][update path] Removing window:', { spaceId, windowId });

    // Mark this window as being removed to prevent it from being restored
    this.removingWindows.add(windowId);

    // Check if we're in a drag operation for this window
    const isDraggingThisWindow = (window as any).__windowDragging && 
                                (window as any).__lastDragWindowId === windowId;
    
    if (isDraggingThisWindow) {
      log.info('[SpaceCRDT] Deferring window removal - drag operation in progress');
      setTimeout(() => this.removeWindow(spaceId, windowId), 200);
      return;
    }

    const spaceKey = this.getKey(spaceId);
    
    // ✅ NEW: Use Y.Map directly for proper CRDT sync
    const doc = this.crdt.getYDoc(spaceKey);
    if (doc) {
      doc.transact(() => {
        const windowsMap = doc.getMap('windows');
        windowsMap.delete(windowId);
        log.info('[SpaceCRDT] ✅ Removed window from CRDT Y.Map:', windowId);
      }, this.localOriginId); // ✅ CRITICAL FIX: Pass origin to prevent same-tab observer loop
    } else {
      log.warn('[SpaceCRDT] ⚠️ No Y.Doc found for space, falling back to old update method');
      // Fallback to old method if doc not found
      this.crdt.update(spaceKey, {
        windows: {
          [windowId]: null
        }
      }, {
        preserveOtherConnections: true,
        preserveNoteConnections: true,
        updateType: 'remove'
      });
    }
    
    // Clear any position updates for this window
    try {
      this.crdt.update(this.getPositionKey(spaceId), {
        [windowId]: null
      }, {
        updateType: 'remove',
        preserveOtherConnections: true,
        preserveNoteConnections: true
      });
    } catch (error) {
      log.warn('[SpaceCRDT] Error removing window from position updates:', error);
    }
    
    // Log this removal for debugging
    log.info(`[SpaceCRDT] Window ${windowId} removed from space ${spaceId}`);
    
    // DON'T immediately save to backend here - let the store handle it
    // after the CRDT update has had time to propagate
    // debouncedSaveSpaceToBackend(spaceId);
    
    // Since saving to backend is critical, also perform an immediate save
    // after a small delay to ensure CRDT has processed the removal
    setTimeout(() => {
      try {
        const store = useSpaceStore.getState();
        const windows = store.windows;
        const settings = store.activeSpace?.settings;
        
        // Log to confirm window is removed from store
        if (windows[windowId]) {
          log.warn(`[SpaceCRDT] Window ${windowId} still exists in store after removal! This may indicate a race condition.`);
          log.info(`[SpaceCRDT] Store still contains:`, Object.keys(windows));
          
          // Check if there are any ongoing operations that might explain this
          const isDragging = (window as any).__windowDragging;
          const isInitializing = (window as any).__SPACE_INITIALIZING__;
          
          if (isDragging || isInitializing) {
            log.info(`[SpaceCRDT] Window persistence might be due to ongoing operations (dragging: ${isDragging}, initializing: ${isInitializing})`);
            // Try again in a bit
                         setTimeout(() => {
               const laterWindows = useSpaceStore.getState().windows;
               if (laterWindows[windowId]) {
                 log.info(`[SpaceCRDT] Window ${windowId} still exists after additional delay - force removing`);
                 const updatedWindows = { ...laterWindows };
                 delete updatedWindows[windowId];
                 useSpaceStore.getState().setWindows(Object.values(updatedWindows));
               }
               
               // Clear from removing set regardless of outcome
               this.removingWindows.delete(windowId);
               log.info(`[SpaceCRDT] Cleared window ${windowId} from removing set (delayed)`);
             }, 500);
          } else {
            // Try to force remove it from the store immediately
            const updatedWindows = { ...windows };
            delete updatedWindows[windowId];
            store.setWindows(Object.values(updatedWindows));
            log.info(`[SpaceCRDT] Forced removal of window ${windowId} from store`);
          }
        } else {
          log.info(`[SpaceCRDT] Confirmed window ${windowId} is removed from store`);
        }
        
        // Clear the window from the removing set since the process is complete
        this.removingWindows.delete(windowId);
        log.info(`[SpaceCRDT] Cleared window ${windowId} from removing set`);
        
        // Save to backend to ensure persistence (use current store state)
        const finalWindows = useSpaceStore.getState().windows;
        const finalActiveWindowId = useSpaceStore.getState().activeWindowId;
        const finalContextEntities = useSpaceStore.getState().contextEntities; // ✅ NEW: Include contextEntities
        spaceService.updateSpace(spaceId, finalWindows, settings || {}, finalActiveWindowId, finalContextEntities)
          .then(() => log.info(`[SpaceCRDT] Successfully persisted window removal to backend`))
          .catch(err => log.error(`[SpaceCRDT] Error persisting window removal to backend:`, err));
      } catch (error) {
        log.error('[SpaceCRDT] Error in delayed backend save after window removal:', error);
      }
    }, 1000); // Increased delay to give more time for CRDT propagation
  }

  addWindow(spaceId: string, windowId: string, updates: Partial<AnyWindowEntity>) {
    log.info('[SpaceCRDT][update path] Adding window:', { spaceId, windowId, updates });
    
    // Debug logging for group windows
    if (updates.appType === 'group') {
      log.info('[SpaceCRDT] 🔍 Adding group window to CRDT:', windowId);
    }
    
    const spaceKey = this.getKey(spaceId);
    
    // ✅ NEW: Use Y.Map directly for proper CRDT sync
    const doc = this.crdt.getYDoc(spaceKey);
    if (doc) {
      doc.transact(() => {
        const windowsMap = doc.getMap('windows');
        windowsMap.set(windowId, updates);
        log.info('[SpaceCRDT] ✅ Added window to CRDT Y.Map:', windowId);
      }, this.localOriginId); // ✅ CRITICAL FIX: Pass origin to prevent same-tab observer loop
    } else {
      log.warn('[SpaceCRDT] ⚠️ No Y.Doc found for space, falling back to old update method');
      // Fallback to old method if doc not found
      this.crdt.update(spaceKey, {
        windows: {
          [windowId]: updates
        }
      }, {
        preserveOtherConnections: true,
        preserveNoteConnections: true,
        updateType: 'update'
      });
    }
    
    // Trigger a backend save to ensure the new window is persisted
    // This is especially important for group windows which need to persist across sessions
    log.info('[SpaceCRDT] Triggering backend save after adding window:', windowId);
    debouncedSaveSpaceToBackend(spaceId);
  }

  updateWindow(spaceId: string, windowId: string, updates: Partial<AnyWindowEntity>) {
    // Check if this is a drag operation to use specialized handling
    const isDragOperation = updates.position && (window as any).__windowDragging;

    // CRITICAL: Detect update loops - but only for non-dragging operations
    // During dragging, we want to send all updates to ensure other clients see the movement
    const lastUpdateKey = `${windowId}:${JSON.stringify(updates.position || {})}`;
    if (!isDragOperation && (window as any).__lastWindowUpdate === lastUpdateKey) {
      log.info(`[CRDT] Skipping duplicate update for window ${windowId}`);
      return;
    }
    (window as any).__lastWindowUpdate = lastUpdateKey;

    // 1. Update local UI for immediate feedback, but NOT the store yet
    if (updates.position) {
      // Only publish if window exists in store (prevents race condition during window creation)
      const store = useSpaceStore.getState();
      if (store.windows[windowId]) {
        // Just dispatch UI event but don't update store (to avoid circular updates)
        windowPositionEventBus.publish(spaceId, windowId, updates.position);
      }
    }
    
    // PERFORMANCE FIX: Add throttling for CRDT updates during drag operations
    if (isDragOperation && shouldThrottleCRDTUpdate(windowId, true)) {
      // Skip CRDT update but still handle local UI updates above
      return;
    }
    
    // Special handling for drag operations - use a dedicated CRDT key with zero debounce
    if (isDragOperation && updates.position) {
      // Use a special key for window position updates during dragging
      // This will use the zero-debounce setting for window positions
      const positionKey = this.getPositionKey(spaceId);
      
      try {
        // Use a specialized format for window position updates for speed
        this.crdt.update(positionKey, {
          [windowId]: updates.position
        }, {
          updateType: 'update',
          preserveOtherConnections: true,
          preserveNoteConnections: true
        });
        
        // Also update the main space data for persistence but with less priority
        this.updateSpaceWithPosition(spaceId, windowId, updates);
        
        // Schedule a debounced save to backend for persistence
        debouncedSaveWindowToSpace(spaceId, windowId, updates);
        
        return; // Skip the regular update path for performance
      } catch (error) {
        log.error(`[CRDT] Error sending position update for window ${windowId}:`, error);
        // Fall through to regular update path if this fails
      }
    }
    
    // 2. Regular update path - send to main space CRDT
    this.updateSpaceWithPosition(spaceId, windowId, updates);
    
    // 3. Make sure changes are persisted to backend
    debouncedSaveWindowToSpace(spaceId, windowId, updates);
  }
  
  // Helper method to update the main space CRDT with window position/data
  private updateSpaceWithPosition(spaceId: string, windowId: string, updates: Partial<AnyWindowEntity>) {
    const spaceKey = this.getKey(spaceId);
    
    try {
      const isDragOperation = updates.position && (window as any).__windowDragging;
      const isDebugMode = (window as any).__CRDT_DEBUG__;
      
      // ✅ NEW: Use Y.Map directly for proper CRDT sync
      const doc = this.crdt.getYDoc(spaceKey);
      if (doc) {
        doc.transact(() => {
          const windowsMap = doc.getMap('windows');
          const existingWindow = windowsMap.get(windowId);
          
          if (existingWindow) {
            // Merge updates into existing window
            const merged = { ...existingWindow, ...updates };
            windowsMap.set(windowId, merged);
            
            if (!isDragOperation || isDebugMode) {
              log.info('[SpaceCRDT] ✅ Updated window in CRDT Y.Map:', {
                windowId,
                updates: Object.keys(updates)
              });
            }
          } else {
            // Window doesn't exist, need to create it with full data
            const fullWindow = useSpaceStore.getState().windows[windowId];
            if (fullWindow) {
              windowsMap.set(windowId, { ...fullWindow, ...updates });
              log.info('[SpaceCRDT] ✅ Created window in CRDT Y.Map with full data:', windowId);
            } else {
              // Just set the updates if we don't have full window
              windowsMap.set(windowId, updates);
              log.warn('[SpaceCRDT] ⚠️ Created window in CRDT with partial data:', windowId);
            }
          }
        }, this.localOriginId); // ✅ CRITICAL FIX: Pass origin to prevent same-tab observer loop
      } else {
        log.warn('[SpaceCRDT] ⚠️ No Y.Doc found for space, falling back to old update method');
        // Fallback to old method if doc not found
        const space = useSpaceStore.getState().activeSpace;
        const existingWindow = space?.windows?.[windowId];
        
        if (!existingWindow && updates.position) {
          const fullWindow = useSpaceStore.getState().windows[windowId];
          if (fullWindow) {
            this.crdt.update(spaceKey, {
              windows: {
                [windowId]: fullWindow
              }
            }, {
              updateType: 'update',
              preserveOtherConnections: true,
              preserveNoteConnections: true,
            });
            return;
          }
        }
        
        this.crdt.update(spaceKey, {
          windows: {
            [windowId]: updates
          }
        }, {
          updateType: 'update',
          preserveOtherConnections: true,
          preserveNoteConnections: true,
        });
      }
    } catch (error) {
      log.error(`[CRDT] Error updating window ${windowId}:`, error);
    }
  }

  updateSettings(spaceId: string, settings: Partial<SpaceSettings>) {
    const spaceKey = this.getKey(spaceId);
    
    // ✅ NEW: Use Y.Map directly for proper CRDT sync
    const doc = this.crdt.getYDoc(spaceKey);
    if (doc) {
      doc.transact(() => {
        const settingsMap = doc.getMap('settings');
        Object.entries(settings).forEach(([key, value]) => {
          settingsMap.set(key, value);
        });
        log.info('[SpaceCRDT] ✅ Updated settings in CRDT Y.Map');
      }, this.localOriginId); // ✅ CRITICAL FIX: Pass origin to prevent same-tab observer loop
    } else {
      log.warn('[SpaceCRDT] ⚠️ No Y.Doc found, falling back to old update method');
      this.crdt.update(spaceKey, {
        settings,
      }, {
        updateType: 'update',
        preserveOtherConnections: true,
        preserveNoteConnections: true,
      });
    }
    
    const listeners = this.settingsListeners.get(spaceId) || [];
    listeners.forEach((listener) => listener(settings));
  }

  // ✅ FIXED: activeWindowId is per-tab/per-user, NOT collaborative data
  // Don't sync via CRDT - just save to backend
  updateActiveWindowId(spaceId: string, activeWindowId: string) {
    log.log('[SpaceCRDT] updateActiveWindowId - this is now a no-op (activeWindowId not in CRDT)');
    log.log('[SpaceCRDT] activeWindowId is per-tab and saved to backend only');
    // Backend saves happen automatically via backendSaveQueue in spaceStore
  }

  disconnectAll() {
    log.info('[SpaceCRDT] Disconnecting all');
    
    // Clean up all observers first
    this.spaceObservers.forEach((cleanup, spaceId) => {
      try {
        cleanup();
        log.info('[SpaceCRDT] Cleaned up observer for space:', spaceId);
      } catch (error) {
        log.warn('[SpaceCRDT] Error cleaning up observer:', error);
      }
    });
    this.spaceObservers.clear();
    
    // Clean up all position receivers
    this.positionReceivers.forEach((cleanup, spaceId) => {
      try {
        cleanup();
        log.info('[SpaceCRDT] Cleaned up position receiver for space:', spaceId);
      } catch (error) {
        log.warn('[SpaceCRDT] Error cleaning up position receiver:', error);
      }
    });
    this.positionReceivers.clear();
    
    this.settingsListeners.clear();
    this.spaceUpdateListeners.clear();

    // Disconnect all space CRDT rooms using the keys captured at initialization.
    // Do NOT recompute via getKey/getPositionKey here — those call getCurrentUserId()
    // which may return null after logout, producing wrong keys that don't match
    // the stored providers.
    const keysSnapshot = new Map(this.initializedSpaceKeys);
    this.initializedSpaces.clear();
    this.initializedSpaceKeys.clear();
    for (const [spaceId, { spaceKey, positionKey }] of keysSnapshot) {
      try {
        this.crdt.disconnect(spaceKey);
      } catch (err) {
        log.warn(`[SpaceCRDT] Error disconnecting space ${spaceId}:`, err);
      }
      try {
        this.crdt.disconnect(positionKey);
      } catch (err) {
        log.warn(`[SpaceCRDT] Error disconnecting window-position ${spaceId}:`, err);
      }
    }
  }

  /**
   * Disconnects a specific tab's connection to a space.
   * This is safer for multi-tab scenarios than disconnectAll.
   */
  disconnectSpace(spaceId: string, tabId: string) {
    log.info(`[SpaceCRDT] 🧹 Disconnecting space ${spaceId} for tab ${tabId}`);
    
    try {
      // 1. Clean up the observer for this space
      const observerCleanup = this.spaceObservers.get(spaceId);
      if (observerCleanup) {
        log.info(`[SpaceCRDT] 🧹 Cleaning up observer for space: ${spaceId}`);
        observerCleanup();
        this.spaceObservers.delete(spaceId);
      }
      
      // 2. Clean up the position receiver for this space
      const positionCleanup = this.positionReceivers.get(spaceId);
      if (positionCleanup) {
        log.info(`[SpaceCRDT] 🧹 Cleaning up position receiver for space: ${spaceId}`);
        positionCleanup();
        this.positionReceivers.delete(spaceId);
      }
      
      // 3. Remove from initialized spaces so it can be re-initialized
      this.initializedSpaces.delete(spaceId);
      log.info(`[SpaceCRDT] 🧹 Removed ${spaceId} from initialized spaces`);

      // 4. Clean up listeners
      this.spaceUpdateListeners.delete(spaceId);
      this.settingsListeners.delete(spaceId);
      this.connectionListeners.delete(spaceId);

      // 5. Disconnect CRDT connections for this space — use stored keys so
      // we don't re-invoke getCurrentUserId() after a potential logout.
      const stored = this.initializedSpaceKeys.get(spaceId);
      this.initializedSpaceKeys.delete(spaceId);
      const spaceKey = stored?.spaceKey ?? this.getKey(spaceId);
      const positionKey = stored?.positionKey ?? this.getPositionKey(spaceId);
      
      try {
        this.crdt.disconnect(spaceKey);
        log.info(`[SpaceCRDT] 🧹 Disconnected CRDT for: ${spaceKey}`);
      } catch (error) {
        log.warn(`[SpaceCRDT] Error disconnecting ${spaceKey}:`, error);
      }
      
      try {
        this.crdt.disconnect(positionKey);
        log.info(`[SpaceCRDT] 🧹 Disconnected CRDT for: ${positionKey}`);
      } catch (error) {
        log.warn(`[SpaceCRDT] Error disconnecting ${positionKey}:`, error);
      }

      // 6. Disconnect presence for this space so stale presence data is not broadcast
      try {
        usePresenceStore.getState().disconnect();
      } catch (error) {
        log.warn(`[SpaceCRDT] Error disconnecting presence for ${spaceId}:`, error);
      }

      log.info(`[SpaceCRDT] ✅ Successfully disconnected space ${spaceId}`);
    } catch (err) {
      log.warn(`[SpaceCRDT] Error disconnecting space ${spaceId} for tab ${tabId}:`, err);
    }
  }

  groupWindows(spaceId: string, windowIds: string[]) {
    const existingWindows = useSpaceStore.getState().windows;
    const firstWindow = existingWindows[windowIds[0]];
    if (!firstWindow) return;

    // Convert first window to parent window with tabs


    // Update the windows


    // this.updateWindows(spaceId, updatedWindows);
  }

  ungroupWindows(windowId: string) {

    const currentWindows = useSpaceStore.getState().windows;
    const window = currentWindows[windowId] as WindowEntity;

    if (!window?.tabs) return;

    // Remove tab properties from window


    // this.updateWindows(spaceId, updatedWindows);
  }

  async initializeSpace(spaceId: string, initialState: UISpace) {
    // CRITICAL: Prevent duplicate initialization
    if (this.initializedSpaces.has(spaceId)) {
      log.log('[SpaceCRDT] ⚠️ Space already initialized, skipping duplicate call:', spaceId);
      return;
    }
    
    const spaceKey = this.getKey(spaceId);
    
    log.log('[SpaceCRDT] 🚀 initializeSpace CALLED for:', spaceId, 'with activeWindowId:', initialState.activeWindowId);
    log.log('[SpaceCRDT] Stack:', new Error().stack?.split('\n')[2]);
    
    log.info('[SpaceCRDT] 🚀 Initializing space:', {
      spaceId,
      backendWindows: Object.keys(initialState.windows || {}).length,
      backendWindowIds: Object.keys(initialState.windows || {}),
      initialActiveWindowId: initialState.activeWindowId
    });
    
    // Clean up any existing observer first (like docsCRDT does)
    const existingObserverCleanup = this.spaceObservers.get(spaceId);
    if (existingObserverCleanup) {
      console.error('[SpaceCRDT] ⚠️⚠️⚠️ Cleaning up existing observer - this indicates duplicate initialization!');
      existingObserverCleanup();
      this.spaceObservers.delete(spaceId);
    }
    
    // Clean up any existing position receiver
    const existingPositionCleanup = this.positionReceivers.get(spaceId);
    if (existingPositionCleanup) {
      log.warn('[SpaceCRDT] ⚠️ Cleaning up existing position receiver before re-initialization:', spaceId);
      existingPositionCleanup();
      this.positionReceivers.delete(spaceId);
    }
    
    // Mark as initialized — capture full CRDT keys now while userId is available.
    // disconnectAll may run after logout when getCurrentUserId() returns null,
    // so we store the keys here rather than recomputing them later.
    this.initializedSpaces.add(spaceId);
    this.initializedSpaceKeys.set(spaceId, {
      spaceKey: this.getKey(spaceId),
      positionKey: this.getPositionKey(spaceId),
    });

    // Set up the window position receiver for real-time position updates
    const positionCleanup = this.setupWindowPositionReceiver(spaceId);
    if (positionCleanup) {
      this.positionReceivers.set(spaceId, positionCleanup);
    }
    
    // ✅ STEP 1: Get or create Y.Doc (like Docs does)
    const doc = this.crdt.ensureInitialized(spaceKey);
    const provider = this.crdt.getProvider(spaceKey);
    
    log.info('[SpaceCRDT] Y.Doc and provider ready:', {
      hasDoc: !!doc,
      hasProvider: !!provider,
      providerConnected: provider?.wsconnected,
      providerSynced: provider?.synced
    });
    
    // ✅ STEP 2: Check if CRDT room already has data (multi-tab scenario)
    const windowsMap = doc.getMap('windows');
    const settingsMap = doc.getMap('settings');
    // ✅ FIXED: activeWindowId not in CRDT (per-tab in localStorage)
    // const activeWindowIdText = doc.getText('activeWindowId'); // REMOVED
    
    const crdtHasData = windowsMap.size > 0;
    const crdtWindowCount = windowsMap.size;
    const backendWindowCount = Object.keys(initialState.windows || {}).length;
    
    log.info('[SpaceCRDT] 🔍 CRDT room state check:', {
      crdtHasData,
      crdtWindows: crdtWindowCount,
      backendWindows: backendWindowCount,
      crdtWindowIds: Array.from(windowsMap.keys()),
      backendWindowIds: Object.keys(initialState.windows || {}),
      providerSynced: provider?.synced
    });
    
    // ✅ STEP 3: Decide - are we joining an existing room or creating it?
    // Only treat the room as "new" once we have confirmed the server state via WS sync.
    // If the provider hasn't synced yet we cannot know whether the server has windows;
    // seeding (and clearing) from backend REST before sync can overwrite other tabs' layouts.
    if (crdtHasData || !provider?.synced) {
      // Either: IDB has local data (another tab / recent reload), OR the WebSocket hasn't synced
      // yet so we don't know the server state. In both cases we must NOT overwrite the CRDT with
      // backend REST state. The setupSpaceObserver below will fire once WS sync completes.
      log.info('[SpaceCRDT] 🤝 Using existing CRDT state (crdtHasData=%s, providerSynced=%s)', crdtHasData, provider?.synced);
      
      // Convert CRDT Y.Map to plain object
      const windowsFromCRDT: Record<string, any> = {};
      windowsMap.forEach((window: any, windowId: string) => {
        try {
          windowsFromCRDT[windowId] = window?.toJSON ? window.toJSON() : JSON.parse(JSON.stringify(window));
        } catch (e) {
          log.error('[SpaceCRDT] Failed to deserialize window from CRDT:', windowId, e);
          windowsFromCRDT[windowId] = window;
        }
      });
      
      // Get settings from CRDT
      const settingsFromCRDT: any = {};
      settingsMap.forEach((value: any, key: string) => {
        settingsFromCRDT[key] = value;
      });

      // Strip per-user navigation fields — same guard as setupSpaceObserver.
      if (settingsFromCRDT.spaceChat) {
        const { tabs: _t, activeTabId: _a, currentThreadIds: _c, ...sharedSpaceChat } = settingsFromCRDT.spaceChat;
        settingsFromCRDT.spaceChat = sharedSpaceChat;
      }

      log.info('[SpaceCRDT] Loaded state from CRDT room: %d windows', Object.keys(windowsFromCRDT).length);
      
      // Update SpaceStore with CRDT state (other tab's state)
      // DON'T update activeWindowId - it's per-tab in localStorage
      useSpaceStore.setState({
        windows: windowsFromCRDT,
        // activeWindowId will be set from localStorage in spaceStore.initialize
        activeSpace: {
          ...initialState,
          windows: windowsFromCRDT,
          settings: Object.keys(settingsFromCRDT).length > 0 ? settingsFromCRDT : initialState.settings,
          // DON'T set activeWindowId from CRDT
        }
      });
      
    } else {
      // WS has synced and the server-side CRDT is confirmed empty — seed from backend REST.
      // This is the "first ever open" case (new space or first device opening this space).
      log.info('[SpaceCRDT] 🎬 CRDT is empty after WS sync — seeding from backend REST state');
      
      // Clear any stale CRDT data and write backend state
      windowsMap.clear();
      Object.entries(initialState.windows || {}).forEach(([windowId, window]) => {
        windowsMap.set(windowId, window);
      });
      
      // Set settings
      settingsMap.clear();
      if (initialState.settings) {
        Object.entries(initialState.settings).forEach(([key, value]) => {
          settingsMap.set(key, value);
        });
      }
      
      // ✅ FIXED: Don't store activeWindowId in CRDT (it's per-tab in localStorage)
      // activeWindowIdText is not used anymore
      
      log.info('[SpaceCRDT] ✅ Initialized CRDT with backend state:', {
        windows: windowsMap.size,
        note: 'activeWindowId in localStorage, not CRDT'
      });
    }
    
    // ✅ STEP 4: Set up CRDT observer for continuous sync (THE MISSING PIECE!)
    this.setupSpaceObserver(spaceId, doc);
    
    log.info('[SpaceCRDT] ✅ Space initialization complete - bidirectional sync active');
  }

  /**
   * Set up CRDT observer for bidirectional sync (like Docs does)
   * This is THE KEY to making multi-tab work properly
   * 
   * CRITICAL FIX: The observer must handle BOTH additions/updates AND deletions.
   * Without deletion logic, closed windows will reappear when CRDT syncs.
   */
  private setupSpaceObserver(spaceId: string, doc: Y.Doc) {
    const spaceKey = this.getKey(spaceId);
    
    // Clean up any existing observer first
    const existingCleanup = this.spaceObservers.get(spaceId);
    if (existingCleanup) {
      console.error('[SpaceCRDT] ⚠️⚠️⚠️ DUPLICATE OBSERVER SETUP DETECTED FOR:', spaceId);
      console.error('[SpaceCRDT] This will cause duplicate updates! Cleaning up old observer first.');
      existingCleanup();
    }
    
    const windowsMap = doc.getMap('windows');
    const settingsMap = doc.getMap('settings');
    // ✅ FIXED: activeWindowId not in CRDT (per-tab in localStorage)
    // const activeWindowIdText = doc.getText('activeWindowId'); // REMOVED
    
    // Track if we're currently updating to prevent loops
    let isUpdatingFromCRDT = false;
    
    // Listen to CRDT changes and sync to SpaceStore
    const observer = (update: Uint8Array, origin: any) => {
      if (isUpdatingFromCRDT) {
        return;
      }
      
      if (origin === this.localOriginId) {
        return;
      }
      
      try {
        isUpdatingFromCRDT = true;
        
        // Deserialize all windows from Yjs in one pass
        const windowsFromCRDT: Record<string, any> = {};
        windowsMap.forEach((window: any, windowId: string) => {
          try {
            windowsFromCRDT[windowId] = window?.toJSON ? window.toJSON() : JSON.parse(JSON.stringify(window));
          } catch (e) {
            log.error('[SpaceCRDT] Failed to deserialize window from CRDT:', windowId, e);
            windowsFromCRDT[windowId] = window;
          }
        });
        
        const settingsFromCRDT: any = {};
        settingsMap.forEach((value: any, key: string) => {
          settingsFromCRDT[key] = value;
        });

        // Strip per-user navigation fields from shared CRDT settings before
        // applying to local store.
        if (settingsFromCRDT.spaceChat) {
          const { tabs: _t, activeTabId: _a, currentThreadIds: _c, ...sharedSpaceChat } = settingsFromCRDT.spaceChat;
          settingsFromCRDT.spaceChat = sharedSpaceChat;
        }
        
        const currentSpaceStore = useSpaceStore.getState();

        // Delete windows that exist in the store but not in the CRDT snapshot
        const crdtWindowIds = new Set(Object.keys(windowsFromCRDT));
        for (const windowId of Object.keys(currentSpaceStore.windows)) {
          if (!crdtWindowIds.has(windowId)) {
            currentSpaceStore._applyLocalWindowDelete(windowId, {
              source: 'crdt',
              syncCRDT: false,
              log: false
            });
          }
        }

        // Apply all window updates in a single setState call instead of one call
        // per window, which avoids N cascading React re-renders for N windows.
        useSpaceStore.setState((state) => ({
          windows: { ...state.windows, ...windowsFromCRDT },
          activeSpace: {
            ...(state.activeSpace || {}),
            windows: { ...state.windows, ...windowsFromCRDT },
            settings: Object.keys(settingsFromCRDT).length > 0
              ? settingsFromCRDT
              : state.activeSpace?.settings,
          } as any
        }));
        
      } finally {
        isUpdatingFromCRDT = false;
      }
    };
    
    // Add observer to Y.Doc
    doc.on('update', observer);
    
    // Store cleanup function
    this.spaceObservers.set(spaceId, () => {
      doc.off('update', observer);
      log.info('[SpaceCRDT] 🧹 Removed observer for space:', spaceId);
    });
    
    log.info('[SpaceCRDT] ✅ CRDT observer set up for bidirectional sync');
  }

  async updateSpaceName(spaceId: string, name: string) {
    log.info('[SpaceCRDT] Updating name:', { spaceId, name });
    const key = this.getKey(spaceId);
    await this.crdt.update(key, {
      data: {
        name
      }
    }, {
      updateType: 'update',
      preserveOtherConnections: true,
      preserveNoteConnections: true,
    });
  }

  // Add a method to get space data directly from CRDT
  async getSpace(spaceId: string): Promise<UISpace | null> {
    try {
      const key = this.getKey(spaceId);
      const data = await this.crdt.get(key);
      // Need to handle the Doc type correctly
      return data as unknown as UISpace;
    } catch (e) {
      log.error('[SpaceCRDT] Error getting space:', e);
      return null;
    }
  }

  /**
   * Per-user Y.Doc key for space window state. Scoping by userId ensures that
   * window state is shared only across the same user's browser tabs/windows —
   * never leaked to other users who happen to have the same org space open.
   */
  private getKey(spaceId: string) {
    const userId = getCurrentUserId();
    return userId ? `space:${spaceId}:${userId}` : `space:${spaceId}`;
  }

  /**
   * Per-user Y.Doc key for real-time window position updates (drag).
   */
  private getPositionKey(spaceId: string) {
    const userId = getCurrentUserId();
    return userId ? `window-position:${spaceId}:${userId}` : `window-position:${spaceId}`;
  }

  private getWindowKey(spaceId: string) {
    // Use a dedicated key for window updates to take advantage of window-specific debounce
    const userId = getCurrentUserId();
    return userId ? `window:space:${spaceId}:${userId}` : `window:space:${spaceId}`;
  }

  /**
   * Per-user Y.Doc key for chat tabs. Using a user-scoped key ensures that
   * tab state is shared only across the same user's browser tabs/windows —
   * never leaked to other users who happen to be in the same space.
   */
  private getChatTabsKey(spaceId: string, userId: string) {
    return `space-chat:${spaceId}:${userId}`;
  }

  /**
   * Write chat tabs to a per-user Y.Doc.
   *
   * The document is keyed as `space-chat:{spaceId}:{userId}` so that each
   * user's tab state is isolated. Changes replicate only to the same user's
   * other browser tabs/windows — never to a different user in the same space.
   *
   * Uses an explicit per-key diff instead of clear()+set(). Y.Map.clear() only
   * deletes keys visible locally at transaction time — keys that exist only on
   * the server (or in another window that hasn't synced yet) are not removed,
   * so they reappear on the next WS merge. Explicit delete() ops propagate as
   * real CRDT tombstones and prevent ghost-tab resurrection.
   */
  writeChatTabs(spaceId: string, userId: string, tabs: any[], activeTabId: string | null): void {
    const key = this.getChatTabsKey(spaceId, userId);
    const doc = this.crdt.ensureInitialized(key);
    const desiredIds = new Set(tabs.map(t => t.id));
    doc.transact(() => {
      const chatTabsMap = doc.getMap<any>('chatTabs');
      const chatMetaMap = doc.getMap<any>('chatMeta');
      // Delete any key present in the map that is not in the desired set.
      // Collecting first avoids mutating the map during iteration.
      const keysToDelete: string[] = [];
      chatTabsMap.forEach((_: any, k: string) => {
        if (!desiredIds.has(k)) keysToDelete.push(k);
      });
      for (const k of keysToDelete) chatTabsMap.delete(k);
      // Upsert each desired tab.
      for (const tab of tabs) {
        chatTabsMap.set(tab.id, tab);
      }
      chatMetaMap.set('activeTabId', activeTabId ?? '');
      // Persist explicit tab order — Y.Map iteration order is not guaranteed,
      // so we store a separate ordered array to reconstruct the correct sequence.
      chatMetaMap.set('tabOrder', tabs.map(t => t.id));
    }, this.localOriginId);
  }

  /**
   * Wait for both the IndexedDB and the WebSocket initial sync for the
   * per-user chat-tabs doc. Resolves as soon as both are done, or after a
   * 2-second timeout (offline / WebSocket unavailable).
   *
   * Use this in initialize() so tab validation sees the fully-merged state
   * (IDB + server) rather than just local IDB, which prevents ghost tabs
   * from reappearing via the post-IDB WebSocket merge.
   */
  async waitForChatTabsSync(spaceId: string, userId: string): Promise<void> {
    const key = this.getChatTabsKey(spaceId, userId);
    // waitForIdbSynced also calls ensureInitialized, which creates the doc and
    // attaches IDB. When IDB fires 'synced', the WS provider is connected.
    await this.crdt.waitForIdbSynced(key);
    const provider = this.crdt.getProvider(key);
    // No provider (unlikely) or already synced — nothing more to wait for.
    if (!provider || (provider as any).synced) return;
    await new Promise<void>((resolve) => {
      const timeout = setTimeout(resolve, 2000);
      provider.once('sync', () => {
        clearTimeout(timeout);
        resolve();
      });
    });
  }

  /**
   * Read current chat tabs from the per-user Y.Doc.
   * Returns null when the Y.Doc is not yet initialized or has no tabs stored.
   */
  readChatTabs(spaceId: string, userId: string): { tabs: any[]; activeTabId: string | null } | null {
    const key = this.getChatTabsKey(spaceId, userId);
    const doc = this.crdt.getYDoc(key);
    if (!doc) return null;
    const chatTabsMap = doc.getMap<any>('chatTabs');
    if (chatTabsMap.size === 0) return null;
    const rawTabs: any[] = [];
    chatTabsMap.forEach((tab: any) => {
      rawTabs.push(tab?.toJSON ? tab.toJSON() : { ...tab });
    });
    const chatMetaMap = doc.getMap<any>('chatMeta');
    const rawActiveTabId = chatMetaMap.get('activeTabId');
    const tabOrder: string[] | undefined = chatMetaMap.get('tabOrder');
    // Sort tabs by the stored order. Tabs missing from tabOrder (e.g. created on an
    // older client that didn't write tabOrder) are appended at the end.
    const tabs = sortTabsByOrder(rawTabs, tabOrder);
    return { tabs, activeTabId: rawActiveTabId || null };
  }


  /**
   * Subscribe to chat-tab changes from the same user's other browser tabs.
   * The callback fires only for remote writes (not for writes from this tab).
   * Returns a cleanup function.
   */
  observeChatTabs(
    spaceId: string,
    userId: string,
    callback: (tabs: any[], activeTabId: string | null) => void,
  ): () => void {
    const key = this.getChatTabsKey(spaceId, userId);
    const doc = this.crdt.ensureInitialized(key);
    const chatTabsMap = doc.getMap<any>('chatTabs');
    const chatMetaMap = doc.getMap<any>('chatMeta');

    const emit = (_event: any, transaction: Y.Transaction) => {
      if (transaction.origin === this.localOriginId) return;
      const rawTabs: any[] = [];
      chatTabsMap.forEach((tab: any) => {
        rawTabs.push(tab?.toJSON ? tab.toJSON() : { ...tab });
      });
      const rawActiveTabId = chatMetaMap.get('activeTabId');
      const tabOrder: string[] | undefined = chatMetaMap.get('tabOrder');
      const tabs = sortTabsByOrder(rawTabs, tabOrder);
      callback(tabs, rawActiveTabId || null);
    };

    chatTabsMap.observe(emit);
    chatMetaMap.observe(emit);
    return () => {
      chatTabsMap.unobserve(emit);
      chatMetaMap.unobserve(emit);
    };
  }

  /**
   * Get connection information for a space
   */
  getSpaceConnectionInfo(spaceId: string): SpaceConnectionInfo {
    const spaceKey = this.getKey(spaceId);
    const positionKey = this.getPositionKey(spaceId);
    
    // Count active connections for this space
    let connectionCount = 0;
    let providerStatus = 'disconnected';
    
    // Check main space provider
    const spaceProvider = this.crdt.getProvider(spaceKey);
    if (spaceProvider) {
      connectionCount++;
      providerStatus = spaceProvider.wsconnected ? 'connected' : 'connecting';
    }
    
    // Check window position provider
    const positionProvider = this.crdt.getProvider(positionKey);
    if (positionProvider && positionProvider !== spaceProvider) {
      connectionCount++;
    }
    
    return {
      spaceId,
      connectionCount,
      isMultiTab: connectionCount > 1,
      providerStatus,
      lastUpdate: Date.now()
    };
  }

  /**
   * Get detailed debug information about CRDT connections
   */
  getDetailedDebugInfo(spaceId: string) {
    const spaceKey = this.getKey(spaceId);
    const positionKey = this.getPositionKey(spaceId);
    
    const spaceProvider = this.crdt.getProvider(spaceKey);
    const positionProvider = this.crdt.getProvider(positionKey);
    
    return {
      spaceId,
      keys: {
        space: spaceKey,
        windowPosition: positionKey
      },
      providers: {
        space: spaceProvider ? {
          connected: spaceProvider.wsconnected,
          room: spaceProvider.roomname,
          url: spaceProvider.url,
          status: spaceProvider.wsconnected ? 'connected' : 'disconnected'
        } : null,
        windowPosition: positionProvider ? {
          connected: positionProvider.wsconnected,
          room: positionProvider.roomname,
          url: positionProvider.url,
          status: positionProvider.wsconnected ? 'connected' : 'disconnected',
          isSameAsSpace: positionProvider === spaceProvider
        } : null
      },
      connectionCount: (spaceProvider ? 1 : 0) + (positionProvider && positionProvider !== spaceProvider ? 1 : 0),
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Subscribe to connection changes for a space. A single 2-second poller is
   * shared across all subscribers for a given spaceId; it's started on the
   * first subscribe and stopped on the last unsubscribe. Previously each
   * subscriber spun up its own interval, which orphaned timers whenever
   * listeners were removed out of order.
   */
  onConnectionChange(spaceId: string, callback: (connectionInfo: SpaceConnectionInfo) => void): () => void {
    if (!this.connectionListeners.has(spaceId)) {
      this.connectionListeners.set(spaceId, []);
    }

    const listeners = this.connectionListeners.get(spaceId)!;
    listeners.push(callback);

    callback(this.getSpaceConnectionInfo(spaceId));

    if (!this.connectionPollIntervals.has(spaceId)) {
      const interval = setInterval(() => {
        const currentListeners = this.connectionListeners.get(spaceId);
        if (!currentListeners || currentListeners.length === 0) return;
        const connectionInfo = this.getSpaceConnectionInfo(spaceId);
        for (const listener of currentListeners) {
          try {
            listener(connectionInfo);
          } catch (error) {
            log.error('[SpaceCRDT] Error in connection listener:', error);
          }
        }
      }, 2000);
      this.connectionPollIntervals.set(spaceId, interval);
    }

    return () => {
      const currentListeners = this.connectionListeners.get(spaceId);
      if (!currentListeners) return;
      const index = currentListeners.indexOf(callback);
      if (index > -1) {
        currentListeners.splice(index, 1);
      }

      if (currentListeners.length === 0) {
        this.connectionListeners.delete(spaceId);
        const interval = this.connectionPollIntervals.get(spaceId);
        if (interval) {
          clearInterval(interval);
          this.connectionPollIntervals.delete(spaceId);
        }
      }
    };
  }
}

// Only export one instance
export const spaceCRDTService = new SpaceCRDTService(crdtServiceWS);
